import Money from "../../ui/Money"
import { useState } from "react"
import { FiChevronDown } from "react-icons/fi"
import { calculateTrademarkFees } from "../../../utils/applicationFees"
import { useFilingSidebarPanel } from "../../ui/FilingSidebarAccordion"

interface Props {
  goodsAndServices: any[] | null
  priorityData: any[] | null
  seniorityData: any[] | null
  // The price only means something once a mark and a class are chosen
  ready?: boolean
}

export default function FeesSection({
  goodsAndServices,
  priorityData,
  seniorityData,
  ready = true
}: Props) {
  const [feesOpen, toggleFees] = useFilingSidebarPanel("fees")

  const fees = calculateTrademarkFees({
    goodsAndServices,
    priorityData,
    seniorityData
  })

  if (!ready) {
    return (
      <aside className="hidden md:block">
        <div className="flex flex-col gap-4">
          <div className="overflow-hidden rounded-md shadow-sm">
            <div className="flex w-full items-center justify-between bg-dantzBlue px-4 py-3 text-sm text-white">
              <span className="font-semibold text-white">Total Fees</span>
              <span className="text-lg font-bold tabular-nums">—</span>
            </div>
            <p className="bg-secondary1 px-4 py-3 text-xs text-white/80">
              The price will be displayed once you select a mark type and at least one class.
            </p>
          </div>
        </div>
      </aside>
    )
  }

  return (
    <aside className="hidden shrink-0 md:block">
      <div className="flex flex-col gap-4">
        <div className="overflow-hidden rounded-md shadow-sm">
          <button
            type="button"
            onClick={toggleFees}
            className="flex w-full cursor-pointer items-center justify-between bg-dantzBlue px-4 py-3 text-sm text-white"
            aria-expanded={feesOpen}
          >
            <span className="font-semibold text-white">Total Fees</span>
            <div className="flex items-center gap-3">
              <span className="text-lg font-bold tabular-nums">
                <Money amount={fees.total} />
              </span>
              <FiChevronDown
                className={`h-4 w-4 transition-transform duration-200 ${
                  feesOpen ? "rotate-180" : ""
                }`}
                aria-hidden="true"
              />
            </div>
          </button>

          {feesOpen && (
            <div className="space-y-6 bg-secondary1 px-4 py-4 text-sm text-white">
              <FeeCategory
                title={`Service Fees (${fees.classCount} ${fees.classCount === 1 ? "class" : "classes"})`}
                subtotal={fees.subtotalServices}
                lines={fees.serviceLines}
              />
              <FeeCategory
                title="Official Fees"
                subtotal={fees.subtotalOfficial}
                lines={fees.officialLines}
              />

              <div className="border-t border-secondary3 pt-4">
                <FeeRow label="Total" value={fees.total} bold />
              </div>
            </div>
          )}
        </div>
      </div>
    </aside>
  )
}

function FeeCategory({
  title,
  subtotal,
  lines
}: {
  title: string
  subtotal: number
  lines: { label: string; amount: number }[]
}) {
  const [open, setOpen] = useState(false)

  return (
    <section className="border-b border-secondary2 pb-3 last:border-0">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between gap-3 text-left"
        aria-expanded={open}
      >
        <span className="font-semibold">{title}</span>
        <span className="flex shrink-0 items-center gap-2">
          <span className="text-xs text-white/80">Subtotal</span>
          <span className="font-semibold tabular-nums"><Money amount={subtotal} /></span>
          <FiChevronDown
            className={`h-4 w-4 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
            aria-hidden="true"
          />
        </span>
      </button>
      {open && (
        <div className="mt-3 space-y-2">
          {lines.map((line) => (
            <FeeRow key={line.label} label={line.label} value={line.amount} />
          ))}
        </div>
      )}
    </section>
  )
}

function FeeRow({
  label,
  value,
  bold = false
}: {
  label: string
  value: number
  bold?: boolean
}) {
  return (
    <div
      className={`grid grid-cols-[1fr_auto] gap-2 ${
        bold ? "font-semibold text-base" : ""
      }`}
    >
      <span>{label}</span>
      <Money amount={value} />
    </div>
  )
}
